import React, { useEffect, useMemo, useRef, useState } from 'react';
import PageWrapper from '../../components/ui/layout/PageWrapper';
import Panel from '../../components/ui/panel/Panel';
import Text from '../../components/ui/text/Text';
import { Row, Col } from 'react-grid-system';
import Input from '../../components/ui/input/Input';
import Button from '../../components/ui/button/Button';
import FormGroup from '../../components/ui/form-group/FormGroup';
import FormLabel from '../../components/ui/form-label/FormLabel';
import { connect } from 'react-redux';
import { AppState } from '../..';
import counterpart from 'counterpart';
import { grpcUnaryCall } from '../../utils/grpc/grpcUtils';
import { ConfigConstants } from '../../utils/config';
import { GetPaymentLogsRequest, GetPaymentLogsResponse, Log } from '../../utils/grpc/generated/Billsby.Protos/billing/private/paymentlogs/payment_logs_pb';
import { PaymentLogServiceClient } from '../../utils/grpc/generated/Billsby.Protos/billing/private/paymentlogs/Payment_logsServiceClientPb';
import NoticePanel from '../../components/ui/notice-panel/NoticePanel';
import CollapsiblePaymentLog from './CollapsiblePaymentLog';

interface IPaymentLogs {
}

const PaymentLogs: React.FC<IPaymentLogs> = () => {

  const [lastName, setLastName] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [lastFourDigits, setLastFourDigits] = useState("");
  const [paymentLogs, setPaymentLogs] = useState<Array<Log>>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showLoadMoreButton, setShowMoreLoadButton] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const pageSize = 20;

  const isButtonEnabled = useMemo(() => {
    const counterLastName = !!lastName ? 1 : 0;
    const counterZipCode = !!zipCode ? 1 : 0;
    const counterLastFourDigits = !!lastFourDigits ? 1 : 0;
    const total = counterLastName + counterZipCode + counterLastFourDigits;
    return total >= 2 ? true : false;
  }, [lastName, zipCode, lastFourDigits]);

  const fetchPaymentLogs = async (page: number = 1) => {
    const getPaymentLogsRequest = new GetPaymentLogsRequest();
    getPaymentLogsRequest.setLastName(lastName);
    getPaymentLogsRequest.setZipCode(zipCode);
    getPaymentLogsRequest.setLastFourDigits(lastFourDigits);
    getPaymentLogsRequest.setPageSize(pageSize);
    getPaymentLogsRequest.setPage(page);
    const paymentLogServiceClient = new PaymentLogServiceClient(ConfigConstants.grpcBaseUrl);
    try {
      const response = await grpcUnaryCall(getPaymentLogsRequest, paymentLogServiceClient, paymentLogServiceClient.getPaymentLogs) as GetPaymentLogsResponse;
      if ((response as any).error || !response.getPaymentLogsList().length) {
        throw (response as any).error;
      }
      else {
        setShowMoreLoadButton(response.getPaymentLogsList().length === pageSize);
        setPaymentLogs(prevState => [...prevState, ...response.getPaymentLogsList()]);
      }
    }
    catch (err) {
      setError(true);
    }
  }

  const handleLoadMorePaymentLogs = async (page: number) => {
    setIsLoadingMore(true);
    try {
      await fetchPaymentLogs(page);
      setCurrentPage(page);
    } finally {
      setIsLoadingMore(false);
    }
  }

  const onSubmit = async (evt: any) => {
    evt.preventDefault();
    setIsLoading(true);
    setPaymentLogs([]);
    setError(false);
    setCurrentPage(1);
    try {
      await fetchPaymentLogs();
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <PageWrapper>
      <form onSubmit={onSubmit}>
        <Panel title="PAYMENT_LOGS">
          <Text content="PAYMENT_LOGS_CONTENT" noMargin />
        </Panel>
        {
          (!!error && !isLoading)
          && <NoticePanel
            type="error"
            icon="far fa-exclamation-circle"
            content={counterpart("PAYMENT_LOGS_ERROR")}
            shouldTranslate={false}
          />
        }

        <Panel title="PAYMENT_LOGS_TRANSACTION_LOOKUP_TITLE">
          <Text content="PAYMENT_LOGS_TRANSACTION_LOOKUP_SUBTITLE" />

          <FormGroup>
            <Row align="center">
              <Col xs={2}>
                <FormLabel target="last-name" content="PAYMENT_LOGS_TRANSACTION_LOOKUP_LASTNAME" noMargin />
              </Col>
              <Col xs={10}>
                <Input
                  id="last-name"
                  placeholder={counterpart("PAYMENT_LOGS_TRANSACTION_LOOKUP_LASTNAME_PLACEHOLDER")}
                  value={lastName}
                  onChange={(evt: any) => setLastName(evt.target.value)}
                />
              </Col>
            </Row>
          </FormGroup>

          <FormGroup>
            <Row align="center">
              <Col xs={2}>
                <FormLabel target="zip-code" content="PAYMENT_LOGS_TRANSACTION_LOOKUP_ZIPCODE" noMargin />
              </Col>
              <Col xs={10}>
                <Input
                  id="zip-code"
                  placeholder={counterpart("PAYMENT_LOGS_TRANSACTION_LOOKUP_ZIPCODE")}
                  value={zipCode}
                  onChange={(evt: any) => setZipCode(evt.target.value)}
                />
              </Col>
            </Row>
          </FormGroup>

          <FormGroup>
            <Row align="center">
              <Col xs={2}>
                <FormLabel target="last-4-digits" content="PAYMENT_LOGS_TRANSACTION_LOOKUP_LASTFOURDIGITS" noMargin />
              </Col>
              <Col xs={10}>
                <Input
                  id="last-4-digits"
                  maxlength="4"
                  placeholder={counterpart("PAYMENT_LOGS_TRANSACTION_LOOKUP_LASTFOURDIGITS")}
                  value={lastFourDigits}
                  onChange={(evt: any) => setLastFourDigits(!isNaN(evt.target.value) ? evt.target.value.replace(/ /g, "") : lastFourDigits)}
                />
              </Col>
            </Row>
          </FormGroup>

          <Button
            id="payment-log-view-button"
            title="PAYMENT_LOGS_VIEW_BUTTON"
            type="submit"
            disabled={isLoading || !isButtonEnabled}
            isLoading={isLoading}
            isFullWidth
          />
        </Panel>
      </form>

      {!!paymentLogs.length && paymentLogs.map((log) => <CollapsiblePaymentLog log={log} key={log.getReference()} />)}
      {!!paymentLogs.length && showLoadMoreButton && <Button id="load-more" isLoading={isLoadingMore} title="PAYMENT_LOGS_LOAD_MORE" isFullWidth onClick={() => handleLoadMorePaymentLogs(currentPage + 1)} />}
    </PageWrapper>
  )
}

export default connect((state: AppState) => ({ intercomReducer: state.intercom }))(PaymentLogs);